import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ChartFactory from "src/charts/ChartFactory";
import { exportDashboardWidgetDataApi } from "src/api/dashboardApi";
import {
  downloadExportFile,
  type ReportExportResponse,
} from "src/helpers/Report";
import WidgetPlaceholder from "./WidgetPlaceholder";
import type { WidgetDownloadOptions } from "./DownloadOptionsMenu";

/** One row of a stage's breakdown: how many cases, and what share of the
 *  whole funnel they represent ("weightage"). Verified against the live
 *  payload: a stage's breakdown rows sum exactly to that stage's own count
 *  and percentage (PA Required: 254 + 1103 + 262 = 1619, and
 *  10.25 + 44.51 + 10.57 = 65.33). */
type BreakdownEntry = {
  count?: number;
  percentage?: number;
};

type FunnelStage = {
  name: string;
  description?: string;
  count: number;
  percentage: number;
  color?: string;
  order?: number;
  // Values are BreakdownEntry objects in the current payload, but older
  // funnels sent bare numbers — both are read, see normalizeBreakdown.
  breakdown?: Record<string, unknown>;
};

type StatCard = {
  label: string;
  value: number | string;
  color?: string;
};

type Insight = {
  variant: "danger" | "success" | "warning" | "info";
  title: string;
  stage?: string;
  detail?: string;
  primary_driver?: string;
  drivers?: AnalyticsDriver[];
};

type FunnelSummary = {
  stat_cards?: StatCard[];
  insights?: Insight[];
  // The current payload sends these three flat figures instead of
  // stat_cards. Read here so they aren't silently dropped — without them
  // the summary strip renders empty for every funnel on the new contract.
  total_referrals?: number;
  therapy_covered?: number;
  success_rate?: number;
};

type AnalyticsDriver = {
  name: string;
  cases: number;
  percentage: number;
};

type LargestDropoffAnalytics = {
  stage?: string;
  lost?: number;
  conversion_rate?: number;
  primary_driver?: string;
  drivers?: AnalyticsDriver[];
};

type StageTatAnalytics = {
  stage: string;
  avg_days?: number;
  case_count?: number;
  included_in_tat?: number;
  missing_timestamp_count?: number;
  not_reached_count?: number;
};

type RankedAnalyticsItem = {
  name?: string;
  reason?: string;
  cases: number;
  percentage?: number;
  root_cause?: string;
};

type DelayedCaseOutlier = {
  case_id: number | string;
  current_stage: string;
  days_waiting: number;
  threshold_days: number;
  payer?: string;
  payer_segment?: string;
  root_cause?: string;
  delayed_stages?: string[];
};

type PayerBreakdowns = {
  top_payers?: RankedAnalyticsItem[];
  payer_tiers?: RankedAnalyticsItem[];
  payer_segments?: RankedAnalyticsItem[];
};

type FunnelAnalytics = {
  largest_dropoff?: LargestDropoffAnalytics;
  stage_tat?: StageTatAnalytics[];
  top_denial_reasons?: RankedAnalyticsItem[];
  payer_breakdowns?: PayerBreakdowns;
  delayed_case_outliers?: DelayedCaseOutlier[];
};

type FunnelApiData = {
  stages?: FunnelStage[];
  summary?: FunnelSummary;
  analytics?: FunnelAnalytics;
};

type FunnelWidgetProps = {
  isLoading: boolean;
  title: string;
  description?: string | null;
  data: unknown;
  widgetId?: string | number;
  dashboardName?: string;
  appliedFilters?: Record<string, unknown>;
  // Backend-declared download capability — merged into ChartFactory's
  // existing native Highcharts menu (Convert to X, table view, etc.) rather
  // than replacing it. Same pattern as ChartWidget.tsx (bar/line_with_bar).
  hasDownloadOptions?: boolean;
  downloadOptions?: WidgetDownloadOptions;
};

type BackendDownloadKind = "csv" | "case_data";

// Acronyms are re-uppercased the same way the case-list export headers do it
// (caseListHeader in widgetExport.ts), so "rx_pa" reads as "Rx PA" and
// "no_pa_required" as "No PA Required" rather than "Rx Pa" / "No Pa Required".
const toLabel = (key: string) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    // "Rx" is deliberately NOT in this list — it is a conventional
    // abbreviation, not an acronym, so "RX PA" would read wrong.
    .replace(/\b(Pa|Bv|Tat|Id|Npi)\b/g, (m) => m.toUpperCase());

const formatNumber = (value?: number | string) =>
  typeof value === "number" ? value.toLocaleString() : value;

const formatDays = (value?: number) =>
  typeof value === "number" ? `${value.toFixed(2)}d` : "-";

const formatPercent = (value?: number) =>
  typeof value === "number" ? `${value.toFixed(1)}%` : "-";

const formatSegment = (value?: string) => (value ? toLabel(value) : "-");

const hasItems = <T,>(items?: T[]) => Array.isArray(items) && items.length > 0;

/**
 * Stat cards for the summary strip.
 *
 * Prefers the backend's own `stat_cards` when present. The current funnel
 * payload doesn't send them — it sends three flat figures instead — so those
 * are mapped here rather than leaving the strip blank. Each is included only
 * when actually a number, so a partial summary renders the part it has.
 */
const summaryStatCards = (summary?: FunnelSummary): StatCard[] => {
  if (hasItems(summary?.stat_cards)) return summary!.stat_cards!;
  if (!summary) return [];

  const cards: StatCard[] = [];
  if (typeof summary.total_referrals === "number") {
    cards.push({ label: "Total Referrals", value: summary.total_referrals });
  }
  if (typeof summary.therapy_covered === "number") {
    cards.push({ label: "Therapy Covered", value: summary.therapy_covered });
  }
  if (typeof summary.success_rate === "number") {
    cards.push({
      label: "Success Rate",
      value: `${parseFloat(summary.success_rate.toFixed(2))}%`,
    });
  }
  return cards;
};


// Still used by LargestDropoffPanel, where bars ARE the right encoding: the
// drop-off drivers are compared against one shared total, so relative length
// is the point. The stage breakdown below is a table instead — see the note
// on BreakdownTable.
function BreakdownBar({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.min((value / total) * 100, 100) : 0;
  return (
    <div className="mb-1">
      <div
        className="d-flex justify-content-between align-items-center mb-1"
        style={{ fontSize: "0.72rem" }}
      >
        <span className="text-body-secondary">{label}</span>
        <span className="fw-semibold">
          {value.toLocaleString()}{" "}
          <span className="text-muted fw-normal">({pct.toFixed(1)}%)</span>
        </span>
      </div>
      <div
        className="rounded-pill overflow-hidden"
        style={{ height: 6, background: "var(--bs-border-color)" }}
      >
        <div
          className="rounded-pill h-100"
          style={{
            width: `${pct}%`,
            background: color,
            transition: "width 0.4s ease",
          }}
        />
      </div>
    </div>
  );
}

// Percentages arrive with up to 2dp ("10.25", "44.51", "100"). Rendered as
// sent rather than rounded to 1dp, because the small weightages here (1.65%)
// are exactly the ones a rounding pass would flatten.
const formatWeight = (value?: number): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${parseFloat(value.toFixed(2))}%`;
};

type BreakdownRow = {
  key: string;
  label: string;
  count?: number;
  percentage?: number;
};

/**
 * Flattens a stage's `breakdown` into table rows.
 *
 * Two shapes are accepted. The current payload maps each key to an object
 * ({med_precert: {count, percentage}}), and older funnels mapped keys
 * straight to a number. A bare number is read as a case count, with its
 * weightage left undefined rather than invented — deriving it would need a
 * denominator this function can't know is the right one.
 */
const normalizeBreakdown = (
  breakdown: Record<string, unknown>,
): BreakdownRow[] =>
  // Return type annotated because flatMap infers it from the first branch
  // alone, which narrows `count` to a required number and rejects the rest.
  Object.entries(breakdown).flatMap(([key, value]): BreakdownRow[] => {
    if (typeof value === "number") {
      return [{ key, label: toLabel(key), count: value }];
    }
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      const entry = value as BreakdownEntry;
      const count = typeof entry.count === "number" ? entry.count : undefined;
      const percentage =
        typeof entry.percentage === "number" ? entry.percentage : undefined;
      // Skip an object carrying neither number — it is some other nested
      // structure, not a breakdown row, and an empty row would just be noise.
      if (count === undefined && percentage === undefined) return [];
      return [{ key, label: toLabel(key), count, percentage }];
    }
    return [];
  });

/**
 * A stage's breakdown as a table: one row per segment, cases and weightage.
 *
 * A table rather than bars because these rows are read as figures, not
 * compared as lengths — the weightages span 1.65% to 44.51%, so most bars
 * would be slivers, and the count and the share are two different numbers
 * that bars can only encode one of.
 *
 * The total row shows the STAGE's own count and percentage, not a sum of the
 * rows above. They agree in the payloads seen so far, but if the backend ever
 * splits them, the stage's figure is the one the funnel itself is drawn from
 * and therefore the one to trust.
 */
// Row accents, sampled from the grouped-bar chart's own stage colours so the
// two widgets read as one family. Each entry is a strong ink for text and the
// soft tint behind it, the same pairing that chart uses for its variance
// chips. Assigned by row position — the breakdown keys vary per stage
// (med_precert / rx_pa / both / no_pa_required), so there is no fixed key to
// map from, and position at least keeps a given stage's colours stable.
const BREAKDOWN_ACCENTS = [
  { ink: "#4D7C0F", tint: "#EDF5DF" },
  { ink: "#C2410C", tint: "#FFF1E6" },
  { ink: "#BE185D", tint: "#FCE7F3" },
  { ink: "#4338CA", tint: "#EEF2FF" },
  { ink: "#0F766E", tint: "#CCFBF1" },
  { ink: "#7E22CE", tint: "#F5E9FF" },
] as const;

const accentFor = (index: number) =>
  BREAKDOWN_ACCENTS[index % BREAKDOWN_ACCENTS.length] ?? BREAKDOWN_ACCENTS[0];

function BreakdownTable({
  breakdown,
  stageCount,
  stagePercentage,
}: {
  breakdown: Record<string, unknown>;
  stageCount: number;
  stagePercentage: number;
}) {
  const rows = normalizeBreakdown(breakdown);
  if (rows.length === 0) return null;

  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0 funnel-breakdown">
        <thead>
          <tr>
            <th scope="col">Segment</th>
            {/* Cases reads left-aligned so it sits directly under its header
                and next to the segment it belongs to. */}
            <th scope="col" className="funnel-breakdown__num">
              Cases
            </th>
            <th scope="col" className="funnel-breakdown__weight">
              Weightage
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const accent = accentFor(index);
            return (
              <tr key={row.key}>
                <td>
                  <span className="funnel-breakdown__segment">
                    <span
                      className="funnel-breakdown__dot"
                      style={{ background: accent.ink }}
                      aria-hidden="true"
                    />
                    {row.label}
                  </span>
                </td>
                <td className="funnel-breakdown__num">
                  {typeof row.count === "number"
                    ? row.count.toLocaleString()
                    : "—"}
                </td>
                <td className="funnel-breakdown__weight">
                  <span
                    className="funnel-breakdown__pill"
                    style={{ background: accent.tint, color: accent.ink }}
                  >
                    {formatWeight(row.percentage)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="funnel-breakdown__total">
            <td>Total</td>
            <td className="funnel-breakdown__num">
              {stageCount.toLocaleString()}
            </td>
            <td className="funnel-breakdown__weight">
              <span className="funnel-breakdown__pill funnel-breakdown__pill--total">
                {formatWeight(stagePercentage)}
              </span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function AnalyticsRankedList({
  items,
  labelKey,
  emptyText,
  limit = 5,
  headerLabel,
}: {
  items?: RankedAnalyticsItem[];
  labelKey: "name" | "reason";
  emptyText: string;
  limit?: number | null;
  headerLabel?: string;
}) {
  const rankedItems = items ?? [];
  const visibleItems = limit == null ? rankedItems : rankedItems.slice(0, limit);

  if (!visibleItems.length) {
    return (
      <div className="text-muted py-2 text-center" style={{ fontSize: "0.72rem" }}>
        {emptyText}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-1">
      {headerLabel && (
        <div
          className="d-flex align-items-center justify-content-between gap-2 text-muted fw-semibold border-bottom pb-1"
          style={{ fontSize: "0.66rem" }}
        >
          <span>{headerLabel}</span>
          <span>Cases</span>
        </div>
      )}
      {visibleItems.map((item, index) => {
        const label = item[labelKey] ?? "Unknown";
        return (
          <div
            key={`${label}-${index}`}
            className="d-flex align-items-start justify-content-between gap-2 border-bottom pb-1"
            style={{ fontSize: "0.72rem" }}
          >
            <div className="text-truncate">
              <span className="text-muted me-1">{index + 1}.</span>
              <span className="text-body">{label}</span>
              {item.root_cause && (
                <div className="text-muted text-truncate" style={{ fontSize: "0.66rem" }}>
                  {item.root_cause}
                </div>
              )}
            </div>
            <div className="text-end flex-shrink-0">
              <span className="fw-semibold">{item.cases.toLocaleString()}</span>
              {typeof item.percentage === "number" && (
                <span className="text-muted ms-1">({formatPercent(item.percentage)})</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

type AnalyticsTab = "dropoff" | "tat" | "denials" | "payers" | "outliers";

function AdditionalAnalyticsPanel({
  analytics,
  activeTab,
  onTabChange,
}: {
  analytics: FunnelAnalytics;
  activeTab: AnalyticsTab;
  onTabChange: (tab: AnalyticsTab) => void;
}) {
  const payerBreakdowns = analytics.payer_breakdowns ?? {};
  const availableTabs: { id: AnalyticsTab; label: string; icon: string }[] = [
    hasItems(payerBreakdowns.top_payers) ||
    hasItems(payerBreakdowns.payer_segments) ||
    hasItems(payerBreakdowns.payer_tiers)
      ? { id: "payers", label: "Payers", icon: "bi-buildings" }
      : null,
    hasItems(analytics.delayed_case_outliers)
      ? { id: "outliers", label: "Outliers", icon: "bi-clock-history" }
      : null,
    hasItems(analytics.stage_tat)
      ? { id: "tat", label: "TAT", icon: "bi-stopwatch" }
      : null,
    hasItems(analytics.top_denial_reasons)
      ? { id: "denials", label: "Denials", icon: "bi-exclamation-triangle" }
      : null,
    analytics.largest_dropoff?.lost != null
      ? { id: "dropoff", label: "Drop", icon: "bi-arrow-down-right-circle" }
      : null,
  ].filter(Boolean) as { id: AnalyticsTab; label: string; icon: string }[];

  if (!availableTabs.length) return null;

  const resolvedActiveTab = availableTabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : availableTabs[0].id;

  return (
    <div className="mt-2">
      <div className="d-flex gap-1 overflow-auto pb-1">
        {availableTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`btn btn-sm d-inline-flex align-items-center gap-1 px-2 py-1 ${
              tab.id === resolvedActiveTab ? "btn-primary" : "btn-outline-secondary"
            }`}
            style={{ fontSize: "0.68rem", whiteSpace: "nowrap" }}
            onClick={() => onTabChange(tab.id)}
          >
            <i className={`bi ${tab.icon}`} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="pt-1">
        {resolvedActiveTab === "dropoff" && (
          <LargestDropoffPanel dropoff={analytics.largest_dropoff} />
        )}
        {resolvedActiveTab === "tat" && (
          <StageTatPanel rows={analytics.stage_tat ?? []} />
        )}
        {resolvedActiveTab === "denials" && (
          <AnalyticsRankedList
            items={analytics.top_denial_reasons}
            labelKey="reason"
            emptyText="No denial reasons returned."
          />
        )}
        {resolvedActiveTab === "payers" && (
          <PayerBreakdownPanel breakdowns={payerBreakdowns} />
        )}
        {resolvedActiveTab === "outliers" && (
          <DelayedOutliersPanel rows={analytics.delayed_case_outliers ?? []} />
        )}
      </div>
    </div>
  );
}

function LargestDropoffPanel({
  dropoff,
}: {
  dropoff?: LargestDropoffAnalytics;
}) {
  if (!dropoff) return null;

  return (
    <div className="rounded px-2 py-2" style={{ background: "rgba(239,68,68,0.08)" }}>
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div>
          <div className="text-danger fw-semibold" style={{ fontSize: "0.75rem" }}>
            {dropoff.stage ?? "Largest Drop"}
          </div>
          <div className="text-muted" style={{ fontSize: "0.68rem" }}>
            Primary driver: {dropoff.primary_driver ?? "Unknown"}
          </div>
        </div>
        <div className="text-end flex-shrink-0" style={{ fontSize: "0.72rem" }}>
          <div className="fw-semibold">{formatNumber(dropoff.lost)} lost</div>
          <div className="text-muted">
            {typeof dropoff.conversion_rate === "number"
              ? `${dropoff.conversion_rate}% conv.`
              : "Conversion unavailable"}
          </div>
        </div>
      </div>
      {hasItems(dropoff.drivers) && (
        <div className="mt-2">
          {dropoff.drivers!.map((driver) => (
            <BreakdownBar
              key={driver.name}
              label={driver.name}
              value={driver.cases}
              total={dropoff.lost ?? driver.cases}
              color="#dc3545"
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StageTatPanel({ rows }: { rows: StageTatAnalytics[] }) {
  const visibleRows = rows.filter((row) => row.stage !== "Referral");

  return (
    <div className="table-responsive">
      <table className="table table-sm mb-0 align-middle" style={{ fontSize: "0.68rem" }}>
        <thead>
          <tr>
            <th className="border-0 text-muted fw-semibold">Stage</th>
            <th className="border-0 text-muted fw-semibold text-end">Avg</th>
            <th className="border-0 text-muted fw-semibold text-end">Included</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => (
            <tr key={row.stage}>
              <td className="text-truncate" style={{ maxWidth: 120 }}>
                {row.stage}
                {typeof row.missing_timestamp_count === "number" &&
                  row.missing_timestamp_count > 0 && (
                    <span className="text-muted ms-1">
                      ({row.missing_timestamp_count} missing)
                    </span>
              )}
              </td>
              <td className="text-end">{formatDays(row.avg_days)}</td>
              <td className="text-end">
                {formatNumber(row.included_in_tat)}/{formatNumber(row.case_count)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayerBreakdownPanel({ breakdowns }: { breakdowns: PayerBreakdowns }) {
  const [activePayerTab, setActivePayerTab] = useState<
    "top_payers" | "payer_segments" | "payer_tiers"
  >("top_payers");

  const payerTabs = [
    hasItems(breakdowns.top_payers)
      ? { id: "top_payers" as const, label: "Payers", labelKey: "name" as const }
      : null,
    hasItems(breakdowns.payer_segments)
      ? { id: "payer_segments" as const, label: "Segments", labelKey: "name" as const }
      : null,
    hasItems(breakdowns.payer_tiers)
      ? { id: "payer_tiers" as const, label: "Tiers", labelKey: "name" as const }
      : null,
  ].filter(Boolean) as {
    id: "top_payers" | "payer_segments" | "payer_tiers";
    label: string;
    labelKey: "name";
  }[];

  if (!payerTabs.length) return null;

  const resolvedTab = payerTabs.some((tab) => tab.id === activePayerTab)
    ? activePayerTab
    : payerTabs[0].id;
  const headerLabelByTab = {
    top_payers: "Payer",
    payer_segments: "Segment",
    payer_tiers: "Tier",
  };

  return (
    <div>
      {payerTabs.length > 1 && (
        <div className="btn-group btn-group-sm mb-2" role="group" aria-label="Payer breakdown">
          {payerTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`btn ${tab.id === resolvedTab ? "btn-secondary" : "btn-outline-secondary"}`}
              style={{ fontSize: "0.66rem" }}
              onClick={() => setActivePayerTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      <AnalyticsRankedList
        items={breakdowns[resolvedTab]}
        labelKey="name"
        emptyText="No payer breakdown returned."
        limit={resolvedTab === "top_payers" ? null : 5}
        headerLabel={headerLabelByTab[resolvedTab]}
      />
    </div>
  );
}

function DelayedOutliersPanel({ rows }: { rows: DelayedCaseOutlier[] }) {
  const outlierGridColumns = "72px minmax(90px, 1.3fr) minmax(80px, 0.9fr) 54px";

  return (
    <div className="d-flex flex-column gap-1">
      <div
        className="d-grid align-items-center gap-2 text-muted fw-semibold border-bottom pb-1"
        style={{ fontSize: "0.66rem", gridTemplateColumns: outlierGridColumns }}
      >
        <span>Case</span>
        <span>Payer</span>
        <span>Segment</span>
        <span className="text-end">Days</span>
      </div>
      {rows.map((row, index) => (
        <div
          key={`${row.case_id}-${row.current_stage}-${index}`}
          className="d-grid align-items-center gap-2 border-bottom pb-1"
          style={{ fontSize: "0.7rem", gridTemplateColumns: outlierGridColumns }}
        >
          <span className="fw-semibold text-truncate">#{row.case_id}</span>
          <span className="text-muted text-truncate" style={{ fontSize: "0.65rem" }}>
            {row.payer ?? "Unknown payer"}
          </span>
          <span className="text-muted text-truncate" style={{ fontSize: "0.65rem" }}>
            {formatSegment(row.payer_segment)}
          </span>
          <span className="fw-semibold text-warning text-end">
            {formatDays(row.days_waiting)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function FunnelWidget({
  isLoading,
  title,
  description,
  data,
  widgetId,
  dashboardName,
  appliedFilters,
  hasDownloadOptions,
  downloadOptions,
}: FunnelWidgetProps) {
  const [selectedStage, setSelectedStage] = useState<FunnelStage | null>(null);
  const [activeAnalyticsTab, setActiveAnalyticsTab] =
    useState<AnalyticsTab>("payers");

  const handleStageClick = useCallback((stage: FunnelStage) => {
    setSelectedStage((prev) => (prev?.name === stage.name ? null : stage));
  }, []);

  // Same backend contract as ChartWidget.tsx's bar/line_with_bar path:
  // "csv" forces top_n:"all" (a download shouldn't be capped at whatever's
  // on screen), "case_data" asks for one row per case via
  // download_level:"detail" and drops top_n.
  const { mutate: downloadBackendData } = useMutation<
    ReportExportResponse,
    Error,
    BackendDownloadKind
  >({
    mutationFn: (kind) => {
      if (!dashboardName || widgetId === undefined || widgetId === null) {
        throw new Error("Missing widget context for download.");
      }
      const filters =
        kind === "case_data"
          ? { ...(appliedFilters ?? {}), download_level: "detail" }
          : { ...(appliedFilters ?? {}), top_n: "all" };
      return exportDashboardWidgetDataApi(
        dashboardName,
        String(widgetId),
        { filters },
        "xlsx",
      );
    },
    onSuccess: async (response, kind) => {
      const normalizedTitle =
        (title || "chart")
          .trim()
          .replace(/[\\/:*?"<>|]+/g, "_")
          .replace(/\s+/g, "_") || "chart";
      const suffix = kind === "case_data" ? "-case-data" : "";
      try {
        await downloadExportFile(
          {
            ...response,
            filename: `${normalizedTitle}${suffix}.${response.format || "xlsx"}`,
          },
          { appliedFilters },
        );
      } catch {
        toast.error("Failed to process download file.");
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to download widget data.");
    },
  });

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={320} />;
  }

  const funnelData = data as FunnelApiData;
  const summary = funnelData?.summary;
  const statCards = summaryStatCards(summary);
  const analytics = funnelData?.analytics;
  const hasBreakdown =
    selectedStage?.breakdown != null &&
    Object.keys(selectedStage.breakdown).length > 0;

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      {/* Chart */}
      <div className="flex-fill" style={{ minHeight: 0 }}>
        <ChartFactory
          chartType="funnel"
          chartId={`${widgetId ?? `${title?.replace(/\s+/g, "-") || "chart"}-funnel`}`}
          title={title}
          description={description}
          data={data}
          fillHeight={true}
          onPointClick={handleStageClick}
          downloadOptions={hasDownloadOptions ? downloadOptions : undefined}
          onDownloadCsvData={() => downloadBackendData("csv")}
          onDownloadCaseData={() => downloadBackendData("case_data")}
        />
      </div>

      {/* Stage breakdown panel */}
      {selectedStage && (
        <div
          className="border-top flex-shrink-0"
          style={{ maxHeight: "45%", overflowY: "auto" }}
        >
          <div className="px-3 pt-2 pb-3">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle flex-shrink-0"
                  style={{
                    width: 10,
                    height: 10,
                    background: selectedStage.color ?? "#6366f1",
                  }}
                />
                <span className="fw-semibold" style={{ fontSize: "0.82rem" }}>
                  {selectedStage.name}
                </span>
                <span
                  className="badge rounded-pill"
                  style={{
                    background: selectedStage.color ?? "#6366f1",
                    fontSize: "0.68rem",
                  }}
                >
                  {selectedStage.count.toLocaleString()} ·{" "}
                  {selectedStage.percentage}%
                </span>
              </div>
              <button
                className="btn-close"
                style={{ fontSize: "0.6rem" }}
                onClick={() => setSelectedStage(null)}
                aria-label="Close breakdown"
              />
            </div>

            {selectedStage.description && (
              <div className="text-muted mb-2" style={{ fontSize: "0.72rem" }}>
                {selectedStage.description}
              </div>
            )}

            {hasBreakdown ? (
              <BreakdownTable
                breakdown={selectedStage.breakdown!}
                stageCount={selectedStage.count}
                stagePercentage={selectedStage.percentage}
              />
            ) : (
              <div
                className="text-muted text-center py-2"
                style={{ fontSize: "0.75rem" }}
              >
                No breakdown available for this stage.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary */}
      {(statCards.length > 0 || summary?.insights || analytics) &&
        !selectedStage && (
        <div
          className="border-top pt-2 mt-1 flex-shrink-0 px-1"
          style={{ maxHeight: "52%", overflowY: "auto" }}
        >
          {statCards.length > 0 && (
            <div className="row g-2 mb-2">
              {statCards.map((card, i) => (
                <div key={i} className="col">
                  <div
                    className="text-center p-2 rounded"
                    style={{ background: "var(--bs-tertiary-bg)" }}
                  >
                    <div className="fw-bold fs-6" style={{ color: card.color }}>
                      {typeof card.value === "number"
                        ? card.value.toLocaleString()
                        : card.value}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                      {card.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {summary?.insights && summary.insights.length > 0 && (
            <div className="d-flex flex-column gap-1">
              {summary.insights.map((insight, i) => {
                const isDanger = insight.variant === "danger";
                return (
                  <div
                    key={i}
                    className="d-flex align-items-start gap-2 px-2 py-1 rounded"
                    style={{
                      background: isDanger
                        ? "rgba(239,68,68,0.08)"
                        : "rgba(16,185,129,0.08)",
                      fontSize: "0.72rem",
                    }}
                  >
                    <i
                      className={`bi ${isDanger ? "bi-arrow-down-circle-fill text-danger" : "bi-check-circle-fill text-success"} flex-shrink-0`}
                      style={{ fontSize: "0.75rem" }}
                    />
                    <div>
                      <span
                        className={`${isDanger ? "text-danger" : "text-success"} fw-semibold`}
                      >
                        {insight.title}:{" "}
                      </span>
                      {insight.stage && (
                        <span className="text-body">{insight.stage}</span>
                      )}
                      {insight.detail && (
                        <span className="text-muted ms-1">
                          · {insight.detail}
                        </span>
                      )}
                      {insight.primary_driver && (
                        <div className="text-muted" style={{ fontSize: "0.66rem" }}>
                          Driver: {insight.primary_driver}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {analytics && (
            <AdditionalAnalyticsPanel
              analytics={analytics}
              activeTab={activeAnalyticsTab}
              onTabChange={setActiveAnalyticsTab}
            />
          )}
        </div>
      )}
    </div>
  );
}
